import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { MatomoService } from './matomo.service';
import { sanitizeMatomoUrl } from './matomo-url';

/** Plafond par chargement de page : une erreur en boucle ne doit pas inonder Matomo. */
const MAX_ERRORS_PER_PAGE_LOAD = 20;
const MAX_MESSAGE_LENGTH = 200;

/**
 * Remonte à Matomo les erreurs interceptées par Angular.
 *
 * Zone.js capture les erreurs de rendu, de souscription et les promesses
 * rejetées pour les confier à l'`ErrorHandler` : elles n'atteignent jamais
 * `window.onerror`, donc `enableJSErrorTracking` ne les voit pas. Même catégorie
 * que ce dernier (« JavaScript Errors ») pour un rapport unique.
 *
 * Action = page (nettoyée) où l'erreur survient, nom = message. Chaque message
 * distinct n'est envoyé qu'une fois par chargement de page.
 */
@Injectable()
export class MatomoErrorHandler extends ErrorHandler {
    // Résolu à la première erreur : l'`ErrorHandler` est créé avant le Router,
    // dont `MatomoService` dépend.
    private readonly injector = inject(Injector);
    private readonly reported = new Set<string>();

    override handleError(error: unknown): void {
        super.handleError(error);

        try {
            this.report(error);
        } catch {
            // La mesure ne doit jamais aggraver une erreur.
        }
    }

    private report(error: unknown): void {
        const matomo = this.injector.get(MatomoService);
        if (!matomo.enabled || this.reported.size >= MAX_ERRORS_PER_PAGE_LOAD) {
            return;
        }

        const message = describeError(error);
        if (this.reported.has(message)) {
            return;
        }
        this.reported.add(message);

        const page = new URL(sanitizeMatomoUrl(window.location.href)).pathname;
        matomo.trackEvent('JavaScript Errors', page, message);
    }
}

function describeError(error: unknown): string {
    // `rejection` : promesse rejetée enveloppée par Zone.js.
    const cause = (error as { rejection?: unknown } | null)?.rejection ?? error;
    if (cause instanceof HttpErrorResponse) {
        // Sans la query (termes de recherche, filtres) ni le corps de la réponse.
        const path = cause.url ? new URL(cause.url, window.location.origin).pathname : '?';
        return `HttpErrorResponse: ${cause.status} ${path}`.slice(0, MAX_MESSAGE_LENGTH);
    }
    const text = cause instanceof Error
        ? `${cause.name}: ${cause.message}`
        : typeof cause === 'string' ? cause : safeStringify(cause);

    // Les messages Angular embarquent parfois des valeurs métier sur plusieurs lignes.
    return text.split('\n')[0].slice(0, MAX_MESSAGE_LENGTH);
}

function safeStringify(value: unknown): string {
    try {
        return JSON.stringify(value) ?? String(value);
    } catch {
        return String(value);
    }
}
